import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Eye,
  EyeOff,
  Loader2,
  Mail,
  Lock,
  LogIn,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useCoreController } from "@/services/coreController";
import { Notification } from "@/components/Notification";
import { getAccountColor } from "@/constants/theme";
import { authService } from "@/services/authService";
import useFindUser from "@/hooks/useFindUser";
import { UserInfo } from "@/interfaces/user-interface";
import { cn } from "@/lib/utils";

const formSchema = z.object({
  email: z.string().email("Email invalide"),
  password: z.string().min(6, "Le mot de passe doit contenir au moins 6 caractères"),
});

type FormValues = z.infer<typeof formSchema>;

function LoginPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const coreController = useCoreController();
  const { setUser } = useFindUser();
  const [notification, setNotification] = useState<{
    isVisible: boolean;
    message: string;
    type: "error" | "success";
  }>({ isVisible: false, message: "", type: "error" });

  const themeColor = getAccountColor(coreController.compte.type);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: "", password: "" },
  });

  const showNotification = (
    message: string,
    type: "error" | "success" = "error"
  ) => {
    setNotification({ isVisible: true, message, type });
  };

  const hideNotification = () => {
    setNotification((prev) => ({ ...prev, isVisible: false }));
  };

  const onSubmit = async (data: FormValues) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await authService.login({
        email: data.email,
        password: data.password,
      });

      if (!response?.success || !response.user) {
        setError(response?.error?.message || "Une erreur est survenue lors de la connexion");
        setUser(undefined);
        setIsLoading(false);
        return;
      }

      const newUser: UserInfo = {
        id: response.user.id,
        email: response.user.email,
        first_name: response.user.displayName?.split(' ')[0],
        last_name: response.user.displayName?.split(' ').slice(1).join(' '),
        balance: response.user.balance,
      };

      setUser(newUser);
      showNotification("Connexion réussie", "success");
      navigate("/dashboard", { replace: true });
      window.location.reload();
    } catch (error: any) {
      setError(
        error.message || "Une erreur est survenue lors de la connexion"
      );
      setUser(undefined);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="min-h-screen bg-gray-50"
    >
      {/* App Bar */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-md mx-auto px-4 py-4 flex items-center">
          <button
            onClick={() => navigate("/")}
            className="p-2 rounded-full bg-white shadow-md hover:shadow-lg transition-shadow"
            style={{ color: themeColor }}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="flex-1 text-center text-lg font-semibold" style={{ color: themeColor }}>
            Connexion
          </h1>
          <div className="w-9" />
        </div>
      </div>

      <div className="max-w-md mx-auto px-5 py-8">
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="rounded-2xl p-6 border bg-white shadow-sm">
            <div className="text-center mb-6">
              <div
                className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                style={{ backgroundColor: `${themeColor}1A` }}
              >
                <LogIn className="w-8 h-8" style={{ color: themeColor }} />
              </div>
              <h2 className="text-xl font-semibold text-gray-800">Vous souhaitez être partenaire</h2>
              <p className="text-sm text-gray-500 mt-1">Connectez-vous pour accéder à votre espace</p>
            </div>

            {error && (
              <div className="mb-4 rounded-xl p-3 bg-red-50 border border-red-200 text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="space-y-5">
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-800">Email</Label>
                <div className="relative">
                  <div
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-lg"
                    style={{ backgroundColor: `${themeColor}1A` }}
                  >
                    <Mail className="w-5 h-5" style={{ color: themeColor }} />
                  </div>
                  <Input
                    {...form.register("email")}
                    type="email"
                    placeholder="m@example.com"
                    className="pl-16 py-6 text-base"
                  />
                </div>
                {form.formState.errors.email && (
                  <p className="text-sm text-red-600">{form.formState.errors.email.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-800">Mot de passe</Label>
                <div className="relative">
                  <div
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-lg"
                    style={{ backgroundColor: `${themeColor}1A` }}
                  >
                    <Lock className="w-5 h-5" style={{ color: themeColor }} />
                  </div>
                  <Input
                    {...form.register("password")}
                    type={showPassword ? "text" : "password"}
                    placeholder="Votre mot de passe"
                    className="pl-16 pr-12 py-6 text-base"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {form.formState.errors.password && (
                  <p className="text-sm text-red-600">{form.formState.errors.password.message}</p>
                )}
              </div>
            </div>
          </div>

          <motion.button
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            type="submit"
            disabled={isLoading}
            className="w-full py-4 rounded-xl text-white font-semibold text-base flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: themeColor }}
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <span>Se connecter</span>
                <LogIn className="w-5 h-5" />
              </>
            )}
          </motion.button>
        </form>

        <div className="mt-6 text-center text-sm text-gray-600">
          Vous n'avez pas de compte ?{" "}
          <button
            onClick={() => navigate("/register")}
            className="font-semibold underline"
            style={{ color: themeColor }}
          >
            Créer mon compte
          </button>
        </div>
      </div>

      <Notification
        message={notification.message}
        type={notification.type}
        isVisible={notification.isVisible}
        onClose={hideNotification}
      />
    </motion.div>
  );
}

export default LoginPage;
